import React, { useCallback, useRef, useState } from "react";
import { Alert, FormHelperText, Typography, useTheme } from "@mui/material";
import { DocumentUpload } from "@components/FileUpload/FileUpload";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import GenericDialog from "@components/GenericDialog/GenericDialog";
import Input from "@components/Input/Input";
import {
  DRAWER_TEXT_MAX_CHARS,
  getDrawerTextAllowedCharactersMessage,
  isDrawerTextAllowed,
  normalizeDrawerText,
} from "@components/SideDrawer/SideDrawer.schema";
import { getSideDrawerStyles } from "@components/SideDrawer/SideDrawer.styles";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { useNotification } from "@context/NotificationContext";
import type { GatepassError } from "@features/gatepass/components/types.ts";
import { uploadFileToAzureBlob } from "@services/azureDataApi";
import { SasTokenPrefix, SasTokenPrivilege } from "@services/user/types";
import { getSasToken } from "@services/userApi";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { useTranslation } from "react-i18next";
import { useCompleteGatepass } from "../../hooks/useCompleteGatepass";
import type { GatepassFlowData } from "../flow/ClaimGatepass";
import { GATEPASS_TASK_ROLES, isTaskForSecurityDepartment } from "../flow/utils";

const UPLOAD_KEY_APPROVE = "gatepassApproveAttachment";
const service = "bff-gatepass";
const TEXTAREA_MAX_CHARS = DRAWER_TEXT_MAX_CHARS;

interface ApproveGatepassDrawerProps {
  isOpen: boolean;
  onClose: (drawerName: string) => void;
  gatepassFlowData?: GatepassFlowData;
  setFakeStatus?: (status: string | null) => void;
}

const ApproveGatepassDrawer: React.FC<ApproveGatepassDrawerProps> = ({
  isOpen,
  onClose,
  gatepassFlowData,
  setFakeStatus,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const styles = getSideDrawerStyles(theme);
  const { notify } = useNotification();
  const taskRole = gatepassFlowData?.task?.role;
  const status = gatepassFlowData?.status ?? "";
  const isFott = taskRole === GATEPASS_TASK_ROLES.FOTT;
  const isSponsorCompany = taskRole === GATEPASS_TASK_ROLES.SPONSOR_COMPANY;
  const isSecurityDepartment = isTaskForSecurityDepartment(status, taskRole);

  const [notes, setNotes] = useState("");
  const [attachmentUrl, setAttachmentUrl] = useState<string | undefined>();
  const [errors, setErrors] = useState<{ notes?: string; files?: string }>({});
  const [error, setError] = useState<null | string>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingPayload, setPendingPayload] = useState<{
    status: "APPROVED";
    reason?: string;
    notes?: string;
    attachments?: Array<{
      originalFileName: string;
      url: string;
      type: string;
    }>;
  } | null>(null);

  const EMPTY_UPLOAD_ENTRIES: UploadEntry[] = [];
  const approveAttachmentFiles = useUploadStore(
    (state) => state.filesByKey[UPLOAD_KEY_APPROVE] ?? EMPTY_UPLOAD_ENTRIES,
  );
  const setFilesForKey = useUploadStore((state) => state.setFilesForKey);
  const clearFilesForKey = useUploadStore((state) => state.clearFilesForKey);
  const FILE_UPLOAD_PROFILES = useFileUploadProfiles();
  const profile = FILE_UPLOAD_PROFILES.sideDrawerApprovalFile;
  const allowedExtensions = profile.format.map((ext) =>
    ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
  );
  const uploadSignatureRef = useRef<string | null>(null);

  const setApproveAttachmentFiles = useCallback(
    (files: UploadEntry[]) => {
      if (!files.length) {
        clearFilesForKey(UPLOAD_KEY_APPROVE);
        return;
      }
      setFilesForKey(UPLOAD_KEY_APPROVE, files);
    },
    [clearFilesForKey, setFilesForKey],
  );

  const documentUploadMutation = useMutation({
    mutationFn: ({ file, documentType }: { file: File; documentType: string }) =>
      runTransaction(
        {
          module: "gatepass",
          screen: "ApproveGatepass",
          action: "upload_gatepass_document",
          entityType: "gatepass_document",
        },
        async (transactionId) => {
          const payloadForSasToken = {
            prefix: SasTokenPrefix.GATEPASS_SECURITY_CLEARANCE,
            contentType: file.type || null,
            privilege: SasTokenPrivilege.WRITE,
            blobUrl: null,
          };
          const { blobUrl, sasToken } = await getSasToken(
            payloadForSasToken,
            ...(transactionId ? [transactionId] : []),
          );
          const [uploadResult] = await uploadFileToAzureBlob({
            file,
            blobUrl,
            sasToken,
            certificateType: documentType,
          });
          const documentUrl = uploadResult?.documentUrl ?? blobUrl;
          return { documentUrl, documentType };
        },
      ),
    onError: (error: Error) => setError(error.message),
  });

  const getFileSignature = (entry: UploadEntry | undefined) => {
    if (!(entry instanceof File)) return null;
    return `${entry.name}-${entry.lastModified}-${entry.size}`;
  };

  const triggerUpload = useCallback(
    (
      entry: UploadEntry | undefined,
      handler: ((file: File) => void) | undefined,
      tracker: React.RefObject<string | null>,
    ) => {
      if (!handler || !(entry instanceof File)) return;
      const signature = getFileSignature(entry);
      if (!signature || tracker.current === signature) return;
      tracker.current = signature;
      handler(entry);
    },
    [],
  );

  const handleDocumentUpload = useCallback((file: File) => {
    documentUploadMutation.mutate(
      { file, documentType: "GATEPASS_ATTACHMENT" },
      {
        onSuccess: ({ documentUrl }: { documentUrl: string }) => {
          setError(null);
          setAttachmentUrl(documentUrl);
        },
      },
    );
  }, []);

  const handleFileUpload = useCallback(
    (newFiles: File[]) => {
      if (newFiles.length > 1) {
        setErrors((prev) => ({ ...prev, files: t("upload.one-document") }));
        return;
      }
      const file = newFiles[0];
      if (!file) return;
      setErrors((prev) => ({ ...prev, files: undefined }));
      setApproveAttachmentFiles([file]);
      triggerUpload(file, handleDocumentUpload, uploadSignatureRef);
    },
    [handleDocumentUpload, setApproveAttachmentFiles, t, triggerUpload],
  );

  const resetFormValues = useCallback(() => {
    setNotes("");
    setAttachmentUrl(undefined);
    setErrors({});
    setApproveAttachmentFiles([]);
    setPendingPayload(null);
    uploadSignatureRef.current = null;
  }, [setApproveAttachmentFiles]);

  const handleSuccess = useCallback(() => {
    setFakeStatus?.(null);
    onClose("approveDrawer");
    resetFormValues();
    setShowConfirmDialog(false);
    notify({
      title: t("gatepass.approve-success", { defaultValue: "Gatepass approved" }),
      type: "success",
    });
  }, [notify, onClose, resetFormValues, setFakeStatus, t]);

  const handleError = useCallback(
    (err: GatepassError) => {
      setFakeStatus?.(null);
      onClose("approveDrawer");
      resetFormValues();
      setShowConfirmDialog(false);
      notify({
        title: t("common.error"),
        description:
          err?.response?.data?.detail ?? err?.message ?? t("common.something-went-wrong"),
        type: "error",
      });
    },
    [notify, onClose, resetFormValues, setFakeStatus, t],
  );

  const { completeGatepass, isPending } = useCompleteGatepass({
    handleSuccess,
    handleError,
  });

  const validateNotes = useCallback(
    (value: string) => {
      if (value.length > TEXTAREA_MAX_CHARS) {
        return t("form-validation.max-length", {
          label: t("gatepass.notes"),
          max: TEXTAREA_MAX_CHARS,
        });
      }

      if (!isDrawerTextAllowed(value)) {
        return getDrawerTextAllowedCharactersMessage(t);
      }

      return undefined;
    },
    [t],
  );

  const handleSubmit = () => {
    if (documentUploadMutation.isPending) {
      notify({
        title: t("common.notification.please-wait-for-upload"),
        type: "warning",
      });
      return;
    }

    const trimmedNotes = normalizeDrawerText(notes);
    setNotes(trimmedNotes);

    const notesError = validateNotes(trimmedNotes);
    if (notesError) {
      setErrors((prev) => ({ ...prev, notes: notesError }));
      return;
    }

    const payload: {
      status: "APPROVED";
      reason?: string;
      notes?: string;
      attachments?: Array<{
        originalFileName: string;
        url: string;
        type: string;
      }>;
    } = { status: "APPROVED" };
    if (trimmedNotes) {
      payload.reason = trimmedNotes;
      payload.notes = trimmedNotes;
    }
    if (attachmentUrl) {
      const file = approveAttachmentFiles[0];
      const originalFileName =
        file instanceof File ? file.name : (file as { name?: string })?.name || "attachment";
      payload.attachments = [
        {
          originalFileName,
          url: attachmentUrl,
          type: "APPROVE_ATTACHMENTS",
        },
      ];
    }
    setPendingPayload(payload);
    setShowConfirmDialog(true);
  };

  const handleConfirmDialogConfirm = () => {
    if (!gatepassFlowData?.id || !pendingPayload) return;
    completeGatepass({
      id: gatepassFlowData.id,
      service,
      payload: pendingPayload,
    });
  };

  const handleConfirmDialogClose = () => {
    setShowConfirmDialog(false);
    setPendingPayload(null);
  };

  const handleCloseDrawer = () => {
    onClose("approveDrawer");
    resetFormValues();
  };

  const isPrintableKey = (e: React.KeyboardEvent) =>
    e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;

  const inputSlotProps = {
    root: {
      className: "rounded-lg text-sm px-4 py-2 border",
      style: {
        borderColor: theme.palette.strokeColor?.primary ?? "#BBC1C5",
        color: theme.palette.text.primary,
      },
    },
    htmlInput: { maxLength: TEXTAREA_MAX_CHARS },
  };

  const sxProps = {
    "& .MuiInputBase-root": {
      borderRadius: "8px",
      borderColor: theme.palette.strokeColor?.primary ?? "#BBC1C5",
      padding: "12px",
      fontSize: theme.typography.body1.fontSize,
      color: theme.palette.text.primary,
    },
    "& textarea": { padding: 0 },
  };

  const charCounterSx = {
    ml: 0,
    mt: 0.5,
    fontSize: 12,
    textAlign: "right",
    color: "text.secondary",
  };

  if (!gatepassFlowData || (!isFott && !isSponsorCompany && !isSecurityDepartment)) {
    return null;
  }

  // FOTT, Sponsor Company, and Security Operator: side drawer with Notes + Attachments + confirmation
  return (
    <div data-testid="approve-gatepass-drawer-form" className="max-h-screen overflow-y-auto">
      <SideDrawer
        isOpen={isOpen}
        title={t("gatepass.approve-drawer-title", { defaultValue: "Approve Gatepass" })}
        onClose={handleCloseDrawer}
        submitLabel={t("gatepass.approve-button", { defaultValue: "Approve" })}
        onSubmit={handleSubmit}
        isSubmitting={isPending}
      >
        <div data-testid="approve-gatepass-drawer-content" className="flex-1 max-w-sm">
          <div className="px-6">
            <Typography
              variant="body1"
              data-testid="approve-gatepass-form-title"
              sx={{ color: "text.primary", pb: 4 }}
            >
              {t("gatepass.approve-drawer-message", {
                defaultValue: "You are about to approve Gatepass",
              })}
              <br />
              <strong>#{gatepassFlowData?.serialNumber ?? gatepassFlowData?.id ?? ""}</strong>
            </Typography>
            <div className="col-span-full mb-0 flex flex-col gap-2 mt-6">
              <CustomFormLabel>{t("gatepass.notes", { defaultValue: "Notes" })}</CustomFormLabel>
              <Input
                placeholder={t("gatepass.notes-placeholder", { defaultValue: "Enter notes" })}
                multiline
                minRows={4}
                fullWidth
                variant="outlined"
                value={notes}
                onKeyDown={(e) => {
                  if (!isPrintableKey(e)) return;
                  if (notes.length >= TEXTAREA_MAX_CHARS) {
                    setErrors((prev) => ({
                      ...prev,
                      notes: t("form-validation.max-length", {
                        label: t("gatepass.notes"),
                        max: TEXTAREA_MAX_CHARS,
                      }),
                    }));
                  }
                }}
                onChange={(e) => {
                  const value = e.target.value;
                  setNotes(value);
                  setErrors((prev) => ({ ...prev, notes: validateNotes(value) }));
                }}
                onBlur={(e) => {
                  const trimmed = normalizeDrawerText(e.target.value);
                  if (trimmed !== notes) {
                    setNotes(trimmed);
                  }
                  setErrors((prev) => ({ ...prev, notes: validateNotes(trimmed) }));
                }}
                slotProps={inputSlotProps}
                sx={sxProps}
                testId="approve-gatepass-notes-input"
              />
              {errors.notes && (
                <p style={styles.errorText} className="text-sm mt-1">
                  {errors.notes}
                </p>
              )}
              <FormHelperText sx={charCounterSx}>
                {notes.length}/{TEXTAREA_MAX_CHARS}
              </FormHelperText>
            </div>
            <div className="col-span-full mt-6">
              <div className={errors.files ? "border border-red-500 rounded-md p-1" : ""}>
                <DocumentUpload
                  label={t("gatepass.attachments", { defaultValue: "Attachments" })}
                  testId="approve-gatepass-attachment-upload"
                  name={UPLOAD_KEY_APPROVE}
                  isSideDrawer
                  maxFiles={1}
                  allowedExtensions={allowedExtensions}
                  onUpload={handleFileUpload}
                  conditions={FILE_UPLOAD_PROFILES.sideDrawerApprovalFile}
                  files={approveAttachmentFiles}
                  setFiles={setApproveAttachmentFiles}
                  uploadDescription={t("upload.dragOrDropDescription")}
                  disabled={documentUploadMutation.isPending}
                />
              </div>
              {errors.files && (
                <p style={styles.errorText} className="text-sm mt-1">
                  {errors.files}
                </p>
              )}
              {approveAttachmentFiles[0] && !errors.files && (
                <div className="flex-1 mt-4">
                  <SingleFile
                    file={approveAttachmentFiles[0]}
                    fieldName={UPLOAD_KEY_APPROVE}
                    setFiles={() => {
                      setErrors((prev) => ({ ...prev, files: undefined }));
                      setApproveAttachmentFiles([]);
                      setAttachmentUrl(undefined);
                      uploadSignatureRef.current = null;
                    }}
                    isLoading={documentUploadMutation.isPending}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </SideDrawer>
      <GenericDialog
        open={showConfirmDialog}
        title={t("gatepass.approve-confirm-title", { defaultValue: "Confirm approve" })}
        elementContent={
          <>
            {t("gatepass.approve-confirm-message", {
              defaultValue: "You are about to approve Gatepass",
            })}{" "}
            <strong>#{gatepassFlowData?.serialNumber ?? gatepassFlowData?.id ?? ""}</strong>.
            <br />
            {t("gatepass.approve-confirm-question", {
              defaultValue: "Are you sure you want to proceed?",
            })}
          </>
        }
        cancelButtonText={t("common.cancel")}
        confirmButtonText={t("common.confirm")}
        handleCloseDialog={handleConfirmDialogClose}
        confirmAction={handleConfirmDialogConfirm}
        testId="approve-gatepass-confirm-dialog"
        isInsideDrawer
      />
      {error ? <Alert severity="error">{error}</Alert> : null}
    </div>
  );
};

export default ApproveGatepassDrawer;
